import { useAppStore } from '../../state/appStore';
import { useProgressStore } from '../../state/progressStore';
import { orderedLevels } from '../../levels';
import { rankKeyFor } from '../../progression/progressModel';
import type { Transmission } from '../../sim/types';
import { useT } from '../hooks';
import { BigButton, Panel, ScreenShell } from '../components';

/** Tarjeta de un itinerario (automático o manual) con su "continuar". */
function TrackCard({ track }: { track: Transmission }) {
  const t = useT();
  const { openIntro, setScreen, setTrack } = useAppStore();
  const progress = useProgressStore((s) => s.progress);
  const levels = orderedLevels(track).filter((l) => !l.freeRoam);
  const next = levels.find(
    (l) => progress.unlockedLevels.includes(l.id) && !progress.bestScores[l.id],
  );
  const done = levels.filter((l) => progress.bestScores[l.id]).length;
  const freeId = track === 'manual' ? 'free-manual' : 'free-lot';
  const accent = track === 'manual' ? 'text-amber-300' : 'text-sky-300';

  return (
    <Panel className="flex flex-col gap-3 px-5 py-4">
      <div className="flex items-center justify-between">
        <div>
          <div className={`text-lg font-bold ${accent}`}>
            {track === 'manual' ? '🦵 ' : '🕹 '}
            {t(`menu.track.${track}`)}
          </div>
          <div className="text-xs text-white/50">{t(`menu.track.${track}.sub`)}</div>
        </div>
        <div className="text-right text-xs text-white/40">
          {done}/{levels.length}
        </div>
      </div>
      {next ? (
        <BigButton onClick={() => openIntro(next.id)}>
          ▶ {t('menu.continue')} · {t(next.titleKey)}
        </BigButton>
      ) : (
        <BigButton onClick={() => openIntro(freeId)}>🏁 {t('menu.trackDone')}</BigButton>
      )}
      <div className="grid grid-cols-2 gap-2">
        <BigButton
          variant="ghost"
          onClick={() => {
            setTrack(track);
            setScreen('levels');
          }}
        >
          🎯 {t('menu.levels')}
        </BigButton>
        <BigButton variant="ghost" onClick={() => openIntro(freeId)}>
          🚗 {t('menu.freeDrive')}
        </BigButton>
      </div>
    </Panel>
  );
}

export function MainMenu() {
  const t = useT();
  const { setScreen, lang, setLang } = useAppStore();
  const progress = useProgressStore((s) => s.progress);

  return (
    <ScreenShell>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="break-words bg-gradient-to-r from-sky-300 to-emerald-300 bg-clip-text text-4xl font-black tracking-tight text-transparent sm:text-5xl">
            {t('app.title')}
          </h1>
          <p className="mt-2 text-lg text-white/80">{t('app.subtitle')}</p>
          <p className="text-sm text-white/50">{t('app.tagline')}</p>
        </div>
        <button
          className="rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/20"
          onClick={() => setLang(lang === 'es' ? 'en' : 'es')}
        >
          {lang === 'es' ? 'EN' : 'ES'}
        </button>
      </div>

      <Panel className="flex items-center justify-between px-5 py-4">
        <div>
          <div className="text-sm text-white/60">{t('levels.xp')}</div>
          <div className="text-2xl font-bold text-amber-300">{progress.xp}</div>
        </div>
        <div className="text-right">
          <div className="text-sm text-white/60">{t(rankKeyFor(progress.xp))}</div>
          <div className="text-sm text-white/40">🏅 {progress.badges.length}</div>
        </div>
      </Panel>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <TrackCard track="manual" />
        <TrackCard track="auto" />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <BigButton variant="ghost" onClick={() => setScreen('theory')}>
          📘 {t('menu.theory')}
        </BigButton>
        <BigButton variant="ghost" onClick={() => setScreen('controls')}>
          🎮 {t('menu.controls')}
        </BigButton>
        <BigButton variant="ghost" onClick={() => setScreen('settings')}>
          ⚙️ {t('menu.settings')}
        </BigButton>
      </div>
    </ScreenShell>
  );
}
